import type { WatermarkSettingsApiData } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import type { UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  zodUpdateCompanyWatermarkSettingsEndpointSchema,
  zodWatermarkSettingsApiSchema,
} from './validations';

/**
 * Updates a company's watermark settings.
 *
 * @param companyId - The ID of the company to update.
 * @param watermarkSettings - An object containing the watermark settings to be updated.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/update_watermark_settings_vadmin_company__company_id__watermark_settings_put
 */
export const updateCompanyWatermarkSettings = async (
  companyId: UUID,
  watermarkSettings: WatermarkSettingsApiData,
) => {
  try {
    return await vestigasApi
      .put(ENDPOINT.COMPANY.WATERMARK_SETTINGS.UPDATE(companyId), {
        json: watermarkSettings,
        validate: {
          body: zodWatermarkSettingsApiSchema,
          pathSegments: zodUpdateCompanyWatermarkSettingsEndpointSchema,
          response: zodSuccessResponseSchema,
        },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error updating company watermark settings',
      error,
    });

    if (error instanceof HTTPError) {
      let errorMessage = '';

      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.company.updateCompanyWatermarkSettings.error404,
            {
              ns: 'data',
              companyId,
            },
          );
          break;
        }

        case 400: {
          errorMessage = i18n.t(
            ($) => $.company.updateCompanyWatermarkSettings.error400,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.company.updateCompanyWatermarkSettings.error.generic,
            { ns: 'data' },
          );
        }
      }

      ToastService.error(errorMessage);
    }

    throw error;
  }
};
